import { get } from 'svelte/store';

import type { SettingsConfig } from 'src/types/Settings';

import { user_data } from 'src/stores/account/user';

import { isCloneDeviceSettingsEnabled } from 'src/helpers/settings/settings_config_helpers';
import { isSubUnit } from 'src/helpers/user_helpers';

// Legacy components
import UserInfo from 'src/components/Settings/AccountSettings/UserInfo.svelte';
import Changelog from 'src/components/Settings/AccountSettings/Changelog.svelte';
import ChangeEmail from 'src/components/Settings/AccountSettings/ChangeEmail.svelte';
import ChangePassword from 'src/components/Settings/AccountSettings/ChangePassword.svelte';
import DataAct from 'src/components/Settings/AccountSettings/DataAct.svelte';
import DeleteAccount from 'src/components/Settings/AccountSettings/DeleteAccount.svelte';
import DeviceConfigurationsTemplates from 'src/components/Settings/AppSettings/DeviceConfigurationsTemplates.svelte';
import ExperimentalFeatureRow from 'src/components/Settings/AccountSettings/ExperimentalFeatures/ExperimentalFeatureRow.svelte';
import EmailNotification from 'src/components/Settings/AccountSettings/Notification/EmailNotification.svelte';
import Language from 'src/components/Settings/AccountSettings/Localization/Language.svelte';
import NewsletterEmail from 'src/components/Settings/AccountSettings/NewsletterEmail.svelte';
import PrivacyPolicy from 'src/components/Settings/AccountSettings/PrivacyPolicy.svelte';
import PushNotification from 'src/components/Settings/AccountSettings/Notification/PushNotification.svelte';
import TermsAndConditions from 'src/components/Settings/AccountSettings/TermsAndConditions.svelte';

// V2 components
import ChangelogV2 from 'src/components/Settings/AccountSettings/ChangelogV2.svelte';
import ChangeEmailV2 from 'src/components/Settings/AccountSettings/ChangeEmailV2.svelte';
import ChangePasswordV2 from 'src/components/Settings/AccountSettings/ChangePasswordV2.svelte';
import DataActV2 from 'src/components/Settings/AccountSettings/DataActV2.svelte';
import DateFormatV2 from 'src/components/Settings/AccountSettings/Localization/DateFormatV2.svelte';
import DeleteAccountV2 from 'src/components/Settings/AccountSettings/DeleteAccountV2.svelte';
import LanguageV2 from 'src/components/Settings/AccountSettings/Localization/LanguageV2.svelte';
import PrivacyPolicyV2 from 'src/components/Settings/AccountSettings/PrivacyPolicyV2.svelte';
import TermsAndConditionsV2 from 'src/components/Settings/AccountSettings/TermsAndConditionsV2.svelte';
import GrantAccess from 'src/components/Settings/AccountSettings/GrantAccess.svelte';
import GrantAccessV2 from 'src/components/Settings/AccountSettings/GrantAccessV2.svelte';

const isInstaller = (): boolean => Boolean(get(user_data)?.installer);

export const userSettingsConfig: SettingsConfig = {
	legacy: [
		{
			id: 'userInfo',
			title: 'label_user_info',
			titleDefault: 'User info',
			component: UserInfo,
		},
		{
			id: 'accessPermissions',
			title: 'label_title_access_permissions',
			titleDefault: 'Access and permissions',
			visible: () => !isInstaller() && !isSubUnit(),
			groups: [
				{
					id: 'generalPermissions',
					title: 'collapse_title_general_permissions',
					titleDefault: 'General Permissions',
					component: GrantAccess,
				},
			],
		},
		{
			id: 'privacySecurity',
			title: 'label_privacy_and_security',
			titleDefault: 'Privacy and Security',
			visible: () => !isInstaller(),
			groups: [
				{
					id: 'notificationsGroup',
					title: 'collapse_title_notifications',
					titleDefault: 'Notifications',
					components: [
						{ id: 'pushNotification', component: PushNotification },
						{
							id: 'emailNotification',
							component: EmailNotification,
						},
						{
							id: 'newsletter',
							component: NewsletterEmail,
							visible: () => !isSubUnit(),
						},
					],
				},
				{
					id: 'privacyGroup',
					title: 'collapse_title_privacy',
					titleDefault: 'Privacy',
					components: [
						{ id: 'privacyPolicy', component: PrivacyPolicy },
						{
							id: 'dataAct',
							component: DataAct,
							visible: () => !isSubUnit(),
						},
						{
							id: 'termsAndConditions',
							component: TermsAndConditions,
						},
						{
							id: 'changelog',
							component: Changelog,
							visible: () => !isSubUnit(),
						},
					],
				},
			],
		},
		{
			id: 'location',
			title: 'label_location',
			titleDefault: 'Location',
			visible: () => !isInstaller(),
			groups: [
				{
					id: 'language',
					title: 'collapse_title_language',
					titleDefault: 'Language',
					component: Language,
				},
			],
		},
		{
			id: 'editUserInfo',
			title: 'label_edit_user_info',
			titleDefault: 'Edit User Info',
			visible: () => !isInstaller(),
			groups: [
				{
					id: 'changeEmail',
					title: 'collapse_title_change_email',
					titleDefault: 'Change email',
					component: ChangeEmail,
				},
				{
					id: 'changePass',
					title: 'collapse_title_change_password',
					titleDefault: 'Change password',
					component: ChangePassword,
				},
				{
					id: 'deleteAcc',
					title: 'delete_account',
					titleDefault: 'Delete Account',
					component: DeleteAccount,
					visible: () => !isSubUnit(),
				},
			],
		},
		{
			id: 'experimentalFeaturesSection',
			title: 'label_labs',
			titleDefault: 'Labs',
			visible: () => !isInstaller() && !isSubUnit(),
			groups: [
				{
					id: 'experimentalFeaturesGroup',
					title: 'collapse_title_experimental_features',
					titleDefault: 'Experimental features',
					components: [
						{
							id: 'experimentalInf2',
							component: ExperimentalFeatureRow,
							componentProps: { featureId: 'inf2' },
						},
						{
							id: 'experimentalMis',
							component: ExperimentalFeatureRow,
							componentProps: { featureId: 'mis' },
						},
						{
							id: 'experimentalCs',
							component: ExperimentalFeatureRow,
							componentProps: { featureId: 'cs' },
						},
						{
							id: 'experimentalNm',
							component: ExperimentalFeatureRow,
							componentProps: { featureId: 'nm' },
						},
						{
							id: 'deviceConfigTemplates',
							component: DeviceConfigurationsTemplates,
							visible: () => isCloneDeviceSettingsEnabled(),
						},
					],
				},
			],
		},
	],
	v2: [
		{
			id: 'userInfo',
			title: 'label_profile_info',
			titleDefault: 'Profile info',
			component: UserInfo,
		},
		{
			id: 'accessPermissions',
			title: 'label_title_access_permissions',
			titleDefault: 'Access and Permissions',
			subtitle: 'settings_access_permissions_subtitle_profile',
			subtitleDefault: 'Manage what users can see or do in your project.',
			visible: () => !isInstaller() && !isSubUnit(),
			groups: [
				{
					id: 'generalPermissionsGroup',
					title: 'collapse_title_general_permissions',
					titleDefault: 'General permissions',
					components: [{ id: 'accAccess', component: GrantAccessV2 }],
				},
			],
		},
		{
			id: 'location',
			title: 'label_localization',
			titleDefault: 'Localisation',
			subtitle: 'settings_localization_subtitle',
			subtitleDefault: 'Manage date format, location, and language.',
			visible: () => !isInstaller(),
			groups: [
				{
					id: 'locationGroup',
					components: [
						{ id: 'dateFormat', component: DateFormatV2 },
						{ id: 'language', component: LanguageV2 },
					],
				},
			],
		},
		{
			id: 'editUserInfo',
			title: 'label_edit_user_info',
			titleDefault: 'Edit User Info',
			subtitle: 'settings_edit_user_info_subtitle_profile',
			subtitleDefault: 'Update the email address and password for your account, or delete it.',
			visible: () => !isInstaller(),
			groups: [
				{
					id: 'editEmailGroup',
					title: 'collapse_title_change_email',
					titleDefault: 'Change email',
					components: [
						{
							id: 'changeEmail',
							component: ChangeEmailV2,
							passIsMobile: true,
						},
					],
				},
				{
					id: 'editPasswordGroup',
					title: 'collapse_title_change_password',
					titleDefault: 'Change password',
					components: [
						{
							id: 'changePass',
							component: ChangePasswordV2,
							passIsMobile: true,
						},
					],
				},
				{
					id: 'deleteAccountGroup',
					title: 'delete_account',
					titleDefault: 'Delete Account',
					components: [{ id: 'deleteAcc', component: DeleteAccountV2 }],
					visible: () => !isSubUnit(),
				},
			],
		},
		{
			id: 'privacySecurity',
			title: 'label_info',
			titleDefault: 'Information',
			subtitle: 'settings_privacy_and_security_subtitle_profile',
			subtitleDefault: 'Manage Security and Privacy settings to protect your account.',
			visible: () => !isInstaller(),
			groups: [
				{
					id: 'privacyGroup',
					components: [
						{ id: 'privacyPolicy', component: PrivacyPolicyV2 },
						{
							id: 'termsAndConditions',
							component: TermsAndConditionsV2,
						},
						{
							id: 'dataAct',
							component: DataActV2,
							visible: () => !isSubUnit(),
						},
						{
							id: 'changelog',
							component: ChangelogV2,
							visible: () => !isSubUnit(),
						},
					],
				},
			],
		},
	],
};
